import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined'
import CloseIcon from '@mui/icons-material/Close'
import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined'
import InboxIcon from '@mui/icons-material/Inbox'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import ReplyIcon from '@mui/icons-material/Reply'
import SendIcon from '@mui/icons-material/Send'
import Avatar from '@mui/joy/Avatar'
import Box from '@mui/joy/Box'
import Button from '@mui/joy/Button'
import CircularProgress from '@mui/joy/CircularProgress'
import Divider from '@mui/joy/Divider'
import FormControl from '@mui/joy/FormControl'
import FormLabel from '@mui/joy/FormLabel'
import IconButton from '@mui/joy/IconButton'
import Input from '@mui/joy/Input'
import List from '@mui/joy/List'
import ListItem from '@mui/joy/ListItem'
import ListItemButton from '@mui/joy/ListItemButton'
import ListItemContent from '@mui/joy/ListItemContent'
import Modal from '@mui/joy/Modal'
import ModalDialog from '@mui/joy/ModalDialog'
import Sheet from '@mui/joy/Sheet'
import Stack from '@mui/joy/Stack'
import Textarea from '@mui/joy/Textarea'
import Typography from '@mui/joy/Typography'
import React, { useCallback, useEffect, useState } from 'react'
import { mailboxApi, MailboxMessage, MailboxThread, SendEmailInput } from '../../api/mailboxApi'

// ─── helpers ─────────────────────────────────────────────────────────────────

const fmtDate = (epochMs: number) => {
    if (!epochMs) return ''
    const d = new Date(epochMs)
    const now = new Date()
    const isToday = d.toDateString() === now.toDateString()
    if (isToday) return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

const initials = (name: string) => {
    const parts = name.trim().split(/\s+/)
    return parts.length >= 2 ? `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase() : name.slice(0, 2).toUpperCase()
}

const senderLabel = (msg: MailboxMessage, myAddress: string) => {
    if (msg.direction === 'outbound') return 'Me'
    return msg.from?.name ?? msg.from?.email ?? ''
}

const colorForEmail = (email: string) => {
    const colors = ['primary', 'success', 'warning', 'danger', 'neutral'] as const
    let hash = 0
    for (const c of email) hash = (hash * 31 + c.charCodeAt(0)) & 0xffffffff
    return colors[Math.abs(hash) % colors.length]
}

// ─── compose modal ───────────────────────────────────────────────────────────

interface ComposeProps {
    open: boolean
    mailboxAddress: string
    inReplyToThreadId?: string
    defaultTo?: string
    defaultSubject?: string
    onSent: (threadId: string) => void
    onClose: () => void
}

function ComposeModal({ open, mailboxAddress, inReplyToThreadId, defaultTo, defaultSubject, onSent, onClose }: ComposeProps) {
    const [to, setTo] = useState(defaultTo ?? '')
    const [subject, setSubject] = useState(defaultSubject ?? '')
    const [body, setBody] = useState('')
    const [sending, setSending] = useState(false)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        if (open) {
            setTo(defaultTo ?? '')
            setSubject(defaultSubject ?? '')
            setBody('')
            setError(null)
        }
    }, [open, defaultTo, defaultSubject])

    const handleSend = async () => {
        const recipients = to
            .split(/[\s,;]+/)
            .filter(Boolean)
            .map((e) => ({ email: e.trim() }))
        if (recipients.length === 0) {
            setError('Add at least one recipient')
            return
        }
        if (!body.trim()) {
            setError('Write a message')
            return
        }
        setSending(true)
        setError(null)
        try {
            const input: SendEmailInput = {
                to: recipients,
                subject: subject || '(no subject)',
                bodyText: body,
                inReplyToThreadId,
                idempotencyKey: `${Date.now()}-${Math.random()}`,
            }
            const res = await mailboxApi.send(input)
            onSent(res.threadId)
        } catch (e) {
            setError((e as Error).message ?? 'Send failed')
        } finally {
            setSending(false)
        }
    }

    return (
        <Modal open={open} onClose={onClose}>
            <ModalDialog sx={{ width: 560, p: 0, overflow: 'hidden' }}>
                {/* header */}
                <Box sx={{ bgcolor: 'neutral.800', px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography level="title-sm" sx={{ color: 'common.white' }}>
                        {inReplyToThreadId ? 'Reply' : 'New message'}
                    </Typography>
                    <IconButton size="sm" variant="plain" sx={{ color: 'neutral.300', '&:hover': { bgcolor: 'neutral.700' } }} onClick={onClose}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>

                <Stack sx={{ px: 2, pb: 2, pt: 1 }} spacing={0.5}>
                    {!inReplyToThreadId && (
                        <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', py: 0.5 }}>
                            <Input size="sm" variant="plain" placeholder="To" value={to} onChange={(e) => setTo(e.target.value)} sx={{ fontSize: 13 }} />
                        </Box>
                    )}
                    <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', py: 0.5 }}>
                        <Input
                            size="sm"
                            variant="plain"
                            placeholder="Subject"
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            disabled={!!inReplyToThreadId}
                            sx={{ fontSize: 13 }}
                        />
                    </Box>
                    <Textarea
                        variant="plain"
                        minRows={8}
                        value={body}
                        onChange={(e) => setBody(e.target.value)}
                        placeholder="Write your message here…"
                        sx={{ fontSize: 13, mt: 0.5 }}
                    />
                    {error && (
                        <Typography level="body-xs" color="danger">
                            {error}
                        </Typography>
                    )}
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 1 }}>
                        <Typography level="body-xs" color="neutral">
                            From: {mailboxAddress}
                        </Typography>
                        <Button size="sm" startDecorator={<SendIcon fontSize="small" />} loading={sending} onClick={handleSend} sx={{ borderRadius: 20 }}>
                            Send
                        </Button>
                    </Stack>
                </Stack>
            </ModalDialog>
        </Modal>
    )
}

// ─── thread view ─────────────────────────────────────────────────────────────

interface ThreadViewProps {
    threadId: string
    mailboxAddress: string
    onBack: () => void
    onReplyDone: () => void
}

function MessageCard({ msg, myAddress, defaultOpen }: { msg: MailboxMessage; myAddress: string; defaultOpen: boolean }) {
    const [open, setOpen] = useState(defaultOpen)
    const isOut = msg.direction === 'outbound'
    const label = senderLabel(msg, myAddress)
    const body = msg.bodyText ?? msg.bodyHtml?.replace(/<[^>]+>/g, ' ') ?? ''

    return (
        <Box sx={{ py: 1 }}>
            {/* collapsed header */}
            <Stack
                direction="row"
                alignItems="center"
                spacing={1.5}
                sx={{ cursor: 'pointer', px: 1, py: 0.5, borderRadius: 'sm', '&:hover': { bgcolor: 'background.level1' } }}
                onClick={() => setOpen((v) => !v)}
            >
                <Avatar size="sm" color={isOut ? 'primary' : colorForEmail(msg.from?.email ?? '')} sx={{ fontSize: 11, width: 28, height: 28 }}>
                    {initials(label || '?')}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" alignItems="baseline" justifyContent="space-between">
                        <Typography level="body-sm" fontWeight="lg" noWrap>
                            {label}
                        </Typography>
                        <Typography level="body-xs" color="neutral" sx={{ ml: 2, flexShrink: 0 }}>
                            {fmtDate(msg.timestamp)}
                        </Typography>
                    </Stack>
                    {!open && (
                        <Typography level="body-xs" color="neutral" noWrap sx={{ maxWidth: 480 }}>
                            {body.slice(0, 120)}
                        </Typography>
                    )}
                </Box>
                <KeyboardArrowDownIcon sx={{ fontSize: 16, color: 'neutral.500', transform: open ? 'rotate(180deg)' : 'none', transition: '0.15s' }} />
            </Stack>

            {/* expanded body */}
            {open && (
                <Box sx={{ ml: 5.5, mt: 1, pr: 2 }}>
                    <Typography level="body-xs" color="neutral" sx={{ mb: 1.5 }}>
                        {isOut ? `To: ${msg.to.map((p) => p.name ?? p.email).join(', ')}` : `To: me (${myAddress})`}
                    </Typography>
                    <Typography level="body-sm" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
                        {body || '(empty)'}
                    </Typography>
                </Box>
            )}
        </Box>
    )
}

function ThreadView({ threadId, mailboxAddress, onBack, onReplyDone }: ThreadViewProps) {
    const [thread, setThread] = useState<MailboxThread | null>(null)
    const [messages, setMessages] = useState<MailboxMessage[]>([])
    const [loading, setLoading] = useState(true)
    const [showCompose, setShowCompose] = useState(false)

    const load = useCallback(async () => {
        setLoading(true)
        try {
            const res = await mailboxApi.getThread(threadId)
            setThread(res.thread)
            setMessages(res.messages)
            if (res.thread.unreadCount > 0) void mailboxApi.markThreadRead(threadId)
        } finally {
            setLoading(false)
        }
    }, [threadId])

    useEffect(() => {
        void load()
    }, [load])

    if (loading)
        return (
            <Stack alignItems="center" justifyContent="center" sx={{ height: 300 }}>
                <CircularProgress size="sm" />
            </Stack>
        )
    if (!thread)
        return (
            <Typography level="body-sm" color="danger" sx={{ p: 3 }}>
                Thread not found.
            </Typography>
        )

    const lastMsg = messages[messages.length - 1]

    return (
        <Box sx={{ p: 3 }}>
            {/* back + subject */}
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                <Button variant="plain" color="neutral" size="sm" onClick={onBack} sx={{ minWidth: 0, px: 1 }}>
                    ← Back
                </Button>
                <Typography level="h4" sx={{ flex: 1 }}>
                    {thread.subject}
                </Typography>
                <Typography level="body-xs" color="neutral">
                    {thread.messageCount} message{thread.messageCount !== 1 ? 's' : ''}
                </Typography>
            </Stack>

            {/* message cards */}
            <Stack spacing={0} divider={<Divider />}>
                {messages.map((m, i) => (
                    <MessageCard key={m.id} msg={m} myAddress={mailboxAddress} defaultOpen={i === messages.length - 1} />
                ))}
            </Stack>

            {/* reply bar */}
            <Box
                sx={{
                    mt: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 'sm',
                    px: 2,
                    py: 1.5,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'background.level1' },
                }}
                onClick={() => setShowCompose(true)}
            >
                <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Avatar size="sm" color="primary" sx={{ fontSize: 11, width: 28, height: 28 }}>
                        Me
                    </Avatar>
                    <Typography level="body-sm" color="neutral">
                        Reply…
                    </Typography>
                </Stack>
            </Box>

            <ComposeModal
                open={showCompose}
                mailboxAddress={mailboxAddress}
                inReplyToThreadId={threadId}
                defaultTo={lastMsg?.from?.email}
                defaultSubject={thread.subject}
                onSent={(tid) => {
                    setShowCompose(false)
                    void load()
                    onReplyDone()
                }}
                onClose={() => setShowCompose(false)}
            />
        </Box>
    )
}

// ─── thread row ───────────────────────────────────────────────────────────────

function ThreadRow({ thread, selected, onClick }: { thread: MailboxThread; selected: boolean; onClick: () => void }) {
    const unread = thread.unreadCount > 0
    const sender = thread.participants[0]
    const label = sender?.name ?? sender?.email ?? 'Unknown'

    return (
        <Box
            onClick={onClick}
            sx={{
                display: 'flex',
                alignItems: 'center',
                px: 2,
                py: 1,
                cursor: 'pointer',
                gap: 1.5,
                bgcolor: selected ? 'primary.softBg' : unread ? 'background.surface' : 'transparent',
                borderLeft: selected ? '3px solid' : '3px solid transparent',
                borderLeftColor: selected ? 'primary.500' : 'transparent',
                '&:hover': { bgcolor: selected ? 'primary.softBg' : 'background.level1' },
            }}
        >
            <Avatar size="sm" color={colorForEmail(sender?.email ?? '')} sx={{ fontSize: 11, width: 32, height: 32, flexShrink: 0 }}>
                {initials(label)}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" alignItems="baseline" justifyContent="space-between">
                    <Typography level="body-sm" fontWeight={unread ? 'lg' : 'md'} noWrap sx={{ flex: 1, mr: 1 }}>
                        {label}
                    </Typography>
                    <Typography level="body-xs" color="neutral" sx={{ flexShrink: 0, fontWeight: unread ? 'lg' : 'md' }}>
                        {fmtDate(thread.lastMessageAt)}
                    </Typography>
                </Stack>
                <Stack direction="row" alignItems="baseline" spacing={0.5}>
                    <Typography level="body-xs" fontWeight={unread ? 'lg' : 'md'} noWrap sx={{ flex: 1 }}>
                        {thread.subject || '(no subject)'}
                        {thread.messageCount > 1 && (
                            <Typography component="span" level="body-xs" color="neutral">
                                {' '}
                                ({thread.messageCount})
                            </Typography>
                        )}
                    </Typography>
                    {unread && <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.500', flexShrink: 0 }} />}
                </Stack>
                <Typography level="body-xs" color="neutral" noWrap>
                    {thread.snippet}
                </Typography>
            </Box>
        </Box>
    )
}

// ─── main page ────────────────────────────────────────────────────────────────

export default function MailboxPage() {
    const [address, setAddress] = useState<string | null>(null)
    const [threads, setThreads] = useState<MailboxThread[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null)
    const [composeOpen, setComposeOpen] = useState(false)

    const loadThreads = useCallback(async () => {
        setLoading(true)
        setError(null)
        try {
            const res = await mailboxApi.listThreads()
            setAddress(res.address)
            setThreads(res.threads)
        } catch (e) {
            setError((e as Error).message ?? 'Failed to load mailbox')
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        void loadThreads()
    }, [loadThreads])

    return (
        <Box sx={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
            {/* ── sidebar ── */}
            <Sheet
                variant="outlined"
                sx={{ width: 220, flexShrink: 0, borderRight: 1, borderColor: 'divider', p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}
            >
                <Button
                    startDecorator={<CreateOutlinedIcon />}
                    onClick={() => setComposeOpen(true)}
                    variant="outlined"
                    color="neutral"
                    sx={{ borderRadius: 20, mb: 1, justifyContent: 'flex-start', boxShadow: 'sm' }}
                >
                    Compose
                </Button>
                <List size="sm" sx={{ gap: 0.25 }}>
                    <ListItem>
                        <ListItemButton selected={!selectedThreadId} onClick={() => setSelectedThreadId(null)} sx={{ borderRadius: 'sm' }}>
                            <InboxIcon sx={{ fontSize: 18, mr: 1 }} />
                            <ListItemContent>Inbox</ListItemContent>
                            {threads.filter((t) => t.unreadCount > 0).length > 0 && (
                                <Typography level="body-xs" fontWeight="lg">
                                    {threads.filter((t) => t.unreadCount > 0).length}
                                </Typography>
                            )}
                        </ListItemButton>
                    </ListItem>
                    <ListItem>
                        <ListItemButton sx={{ borderRadius: 'sm', color: 'neutral.500' }}>
                            <SendIcon sx={{ fontSize: 18, mr: 1 }} />
                            <ListItemContent>Sent</ListItemContent>
                        </ListItemButton>
                    </ListItem>
                    <ListItem>
                        <ListItemButton sx={{ borderRadius: 'sm', color: 'neutral.500' }}>
                            <ArchiveOutlinedIcon sx={{ fontSize: 18, mr: 1 }} />
                            <ListItemContent>Archive</ListItemContent>
                        </ListItemButton>
                    </ListItem>
                </List>
                <Box sx={{ mt: 'auto' }}>
                    <Divider sx={{ mb: 1 }} />
                    <Typography level="body-xs" color="neutral" sx={{ wordBreak: 'break-all', fontFamily: 'monospace', fontSize: 10, lineHeight: 1.4 }}>
                        {address ?? '—'}
                    </Typography>
                </Box>
            </Sheet>

            {/* ── main area ── */}
            <Box sx={{ flex: 1, overflow: 'auto' }}>
                {selectedThreadId && address ? (
                    <ThreadView
                        threadId={selectedThreadId}
                        mailboxAddress={address}
                        onBack={() => {
                            setSelectedThreadId(null)
                            void loadThreads()
                        }}
                        onReplyDone={() => void loadThreads()}
                    />
                ) : (
                    <>
                        {loading && (
                            <Stack alignItems="center" sx={{ mt: 6 }}>
                                <CircularProgress size="sm" />
                            </Stack>
                        )}
                        {error && (
                            <Typography level="body-sm" color="danger" sx={{ p: 3 }}>
                                {error}
                            </Typography>
                        )}
                        {!loading && !error && threads.length === 0 && (
                            <Stack alignItems="center" justifyContent="center" sx={{ height: 300 }}>
                                <InboxIcon sx={{ fontSize: 48, color: 'neutral.300', mb: 1 }} />
                                <Typography level="body-sm" color="neutral">
                                    No messages yet
                                </Typography>
                                <Typography level="body-xs" color="neutral">
                                    Compose an email to a broker
                                </Typography>
                            </Stack>
                        )}
                        <Stack divider={<Divider />} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                            {threads.map((t) => (
                                <ThreadRow key={t.id} thread={t} selected={t.id === selectedThreadId} onClick={() => setSelectedThreadId(t.id)} />
                            ))}
                        </Stack>
                    </>
                )}
            </Box>

            {/* ── compose modal ── */}
            {address && (
                <ComposeModal
                    open={composeOpen}
                    mailboxAddress={address}
                    onSent={(tid) => {
                        setComposeOpen(false)
                        void loadThreads()
                        setSelectedThreadId(tid)
                    }}
                    onClose={() => setComposeOpen(false)}
                />
            )}
        </Box>
    )
}
